// Product create & edit page — 2-column layout (ShopTrack standard).
// URL: /products/new (Tạo mới) & /products/:id/edit (Chỉnh sửa).

import React, { useState, useEffect, useMemo } from "react"
import { useNavigate, useParams, Link } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import {
  ArrowLeft,
  Package,
  Tag,
  Truck,
  Boxes,
  Loader2,
  Sparkles,
  CheckCircle2,
  TrendingUp,
  Info,
  Layers,
  UploadCloud,
  Image as ImageIcon,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import { MobileActionBar } from "@/components/ui/mobile-action-bar"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { MoneyInput } from "@/components/ui/money-input"
import { get, post, patch, uploadFile } from "@/lib/api"
import { money, num, parseDecimal, sanitizeDecimalInput } from "@/lib/format"
import { cn } from "@/lib/utils"
import { useUnitCan } from "@/lib/unitAccess"
import { chargeableFromParts, volumetricWeightGram } from "@shoptrack/common/price-grid"
import { useVolumetricRule } from "@/lib/volumetricRule"

interface ProductData {
  id: string
  sku: string
  name: string
  price: number
  cost: number
  stock: number
  unit: string
  weightGram: number
  lengthCm?: number | null
  widthCm?: number | null
  heightCm?: number | null
  active?: boolean
  imageUrl?: string | null
  images?: string[]
}

const COMMON_UNITS = ["cái", "bộ", "hộp", "chiếc", "gói", "đôi", "kg", "chai"]
/** DB lưu cm nguyên (Int) — làm tròn đến cm gần nhất, tối thiểu 1. */
const toSavedCm = (n: number | null) => (n ? Math.max(1, Math.round(n)) : null)

const WEIGHT_PRESETS = [
  { label: "200g", gram: 200 },
  { label: "300g", gram: 300 },
  { label: "500g", gram: 500 },
  { label: "1kg", gram: 1000 },
  { label: "1.5kg", gram: 1500 },
  { label: "2kg", gram: 2000 },
]

export default function ProductCreatePage() {
  const { id } = useParams<{ id?: string }>()
  const isEdit = Boolean(id)
  const nav = useNavigate()
  const qc = useQueryClient()

  const [sku, setSku] = useState("")
  const [name, setName] = useState("")
  const [unit, setUnit] = useState("cái")
  const [price, setPrice] = useState(0)
  const [cost, setCost] = useState(0)
  const [stock, setStock] = useState(0)
  // Tồn kho lúc mở form sửa — không đổi thì không gửi, để khỏi ghi đè kho vừa bị trừ / trả lại ở nơi khác
  const [loadedStock, setLoadedStock] = useState<number | null>(null)
  const [weightGram, setWeightGram] = useState(300)
  // Kích thước giữ dạng chữ khi gõ (để gõ được "12,5"); số lưu = làm tròn đến cm
  const [lengthText, setLengthText] = useState("")
  const [widthText, setWidthText] = useState("")
  const [heightText, setHeightText] = useState("")
  const lengthCm = toSavedCm(parseDecimal(lengthText))
  const widthCm = toSavedCm(parseDecimal(widthText))
  const heightCm = toSavedCm(parseDecimal(heightText))
  const roundedDims = [lengthText, widthText, heightText].some((t) => {
    const n = parseDecimal(t)
    return n !== null && !Number.isInteger(n)
  })
  const [imageUrl, setImageUrl] = useState("")
  // Nhân viên không có quyền xem tiền (docs/KE_HOACH_THANH_VIEN_DON_VI.md): không thấy / không sửa giá vốn và lãi —
  // máy chủ không trả giá vốn và bỏ qua giá vốn nhân viên gửi lên
  const { seesMoney } = useUnitCan()
  const [isUploadingImage, setIsUploadingImage] = useState(false)
  const [showUrlInput, setShowUrlInput] = useState(false)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  // Query existing product when in edit mode
  const { data: productData, isLoading: isFetchingProduct } = useQuery({
    queryKey: ["product", id],
    queryFn: () => get<{ product: ProductData }>(`/products/${id}`),
    enabled: isEdit,
    // Luôn lấy tồn kho mới khi mở form; không tải lại lúc quay lại tab (sẽ xoá chữ đang gõ)
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (productData?.product) {
      const p = productData.product
      setSku(p.sku || "")
      setName(p.name || "")
      setUnit(p.unit || "cái")
      setPrice(p.price || 0)
      setCost(p.cost || 0)
      setStock(p.stock || 0)
      setLoadedStock(p.stock || 0)
      setWeightGram(p.weightGram || 300)
      setLengthText(p.lengthCm ? String(p.lengthCm) : "")
      setWidthText(p.widthCm ? String(p.widthCm) : "")
      setHeightText(p.heightCm ? String(p.heightCm) : "")
      setImageUrl(p.imageUrl || "")
    }
  }, [productData])

  async function handleFileUpload(file: File) {
    if (!file) return
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"]
    if (!allowedTypes.includes(file.type)) {
      toast.error("Chỉ nhận ảnh JPG, PNG, WebP hoặc GIF.")
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Dung lượng ảnh vượt quá 5MB.")
      return
    }

    setIsUploadingImage(true)
    try {
      const formData = new FormData()
      formData.append("file", file)
      const res = await uploadFile<{ url: string; filename: string }>("/products/upload-image", formData)
      setImageUrl(res.url)
      toast.success("Tải ảnh sản phẩm lên thành công!")
    } catch (err: any) {
      toast.error(err?.message || "Không thể tải ảnh lên.")
    } finally {
      setIsUploadingImage(false)
    }
  }

  // Real-time calculations
  const profit = useMemo(() => price - cost, [price, cost])
  const profitMargin = useMemo(() => {
    if (price <= 0) return 0
    return ((profit / price) * 100).toFixed(1)
  }, [price, profit])

  // Cân quy đổi: D×R×C (cm) / hệ số Super Admin đặt (mặc định 6000) — dùng chung hàm với server (tính theo mm).
  // Super Admin tắt quy đổi → không hiện, tính cước theo cân thực
  const volumetricRule = useVolumetricRule()
  const volumetricGram = useMemo(() => {
    if (!volumetricRule.enabled || !lengthCm || !widthCm || !heightCm) return null
    return volumetricWeightGram({ lengthMm: lengthCm * 10, widthMm: widthCm * 10, heightMm: heightCm * 10 }, volumetricRule)
  }, [lengthCm, widthCm, heightCm, volumetricRule])

  // Quy tắc BEST: cân thực ≤ 3kg và quy đổi ≤ 5kg → cân thực; còn lại lấy số lớn hơn
  const billableWeightGram = useMemo(() => {
    if (!volumetricGram || !(weightGram > 0)) return weightGram
    return chargeableFromParts(Math.round(weightGram), volumetricGram, volumetricRule)
  }, [weightGram, volumetricGram, volumetricRule])

  // Random SKU generator
  function handleGenerateSku() {
    const randomSuffix = Math.random().toString(36).substring(2, 7).toUpperCase()
    setSku(`SP-${randomSuffix}`)
  }

  // Mutation to save product
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!sku.trim()) throw new Error("Vui lòng nhập mã SKU")
      if (!name.trim()) throw new Error("Vui lòng nhập tên sản phẩm")

      const payload = {
        sku: sku.trim().toUpperCase(),
        name: name.trim(),
        price: Math.max(0, Math.round(price)),
        cost: Math.max(0, Math.round(cost)),
        stock: Math.max(0, Math.round(stock)),
        unit: unit.trim() || "cái",
        weightGram: Math.max(1, Math.round(weightGram)),
        lengthCm,
        widthCm,
        heightCm,
        imageUrl: imageUrl.trim() || null,
      }

      if (isEdit && id) {
        const { stock: nextStock, ...rest } = payload
        const body = nextStock === loadedStock ? rest : payload
        // Không thấy giá vốn → không gửi (giữ giá vốn chủ đã nhập)
        if (!seesMoney) {
          const { cost: _cost, ...withoutCost } = body
          return patch<{ product: ProductData }>(`/products/${id}`, withoutCost)
        }
        return patch<{ product: ProductData }>(`/products/${id}`, body)
      } else {
        return post<{ product: ProductData }>("/products", payload)
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["products"] })
      if (id) qc.invalidateQueries({ queryKey: ["product", id] })
      toast.success(isEdit ? "Cập nhật sản phẩm thành công!" : "Đã thêm sản phẩm thành công!")
      nav("/products")
    },
    onError: (err: any) => {
      toast.error(err?.message || "Không thể lưu sản phẩm.")
    },
  })

  // Trạng thái bán: sản phẩm đã "Ngừng bán" thì nói đúng như vậy và cho bật lại ngay tại đây
  const isActive = isEdit ? productData?.product?.active !== false : true
  const reactivateMutation = useMutation({
    mutationFn: () => patch<{ product: ProductData }>(`/products/${id}`, { active: true }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["products"] })
      qc.invalidateQueries({ queryKey: ["products-active"] })
      qc.invalidateQueries({ queryKey: ["product", id] })
      toast.success("Đã bật lại bán sản phẩm")
    },
    onError: (err: any) => toast.error(err?.message || "Không bật lại được sản phẩm"),
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    saveMutation.mutate()
  }

  if (isEdit && isFetchingProduct) {
    return (
      <div className="py-24 text-center text-muted">
        <Loader2 className="size-8 animate-spin mx-auto mb-3 text-accent" />
        <p className="text-sm font-medium">Đang tải thông tin sản phẩm…</p>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-0 sm:px-4 py-1 sm:py-6 space-y-4">
      {/* ─── Top Header & Navigation ─── */}
      <div className="mx-auto flex w-full max-w-5xl flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link
            to="/products"
            className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap px-3 py-1.5 min-h-11 desktop:min-h-0 rounded-lg border border-rule bg-raise hover:bg-paperSoft text-xs font-medium text-inkSoft hover:text-ink transition-colors shadow-2xs"
          >
            <span>Quay lại</span>
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <h1>
                {isEdit ? "Sửa sản phẩm" : "Thêm sản phẩm"}
              </h1>
            </div>
          </div>
        </div>
        {/* Máy tính: hành động ở đầu trang — form một cột cân đối, không cần cột phải */}
        <div className="hidden desktop:flex items-center gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/products">Hủy</Link>
          </Button>
          <Button type="submit" form="product-form" size="sm" disabled={saveMutation.isPending} className="min-w-36 font-semibold">
            {saveMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : "Lưu"}
          </Button>
        </div>
      </div>

      {/* ─── Main Form (2-column layout) ─── */}
      <form id="product-form" onSubmit={handleSubmit}>
        <div>
          {/* ─── CỘT TRÁI: FORM NHẬP LIỆU (8 Cột) — 2 khối gọn: thông tin + ảnh, giá / kho + vận chuyển ─── */}
          <div className="mx-auto max-w-5xl space-y-3">
            {/* KHỐI 1: Ảnh (ô vuông bấm / kéo thả) + SKU, đơn vị, tên */}
            <div className="rounded-xl bg-raise p-3 sm:p-4 space-y-2.5 shadow-card">
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) handleFileUpload(file)
                  e.target.value = ""
                }}
              />
              <div className="grid grid-cols-[5.5rem_minmax(0,1fr)] gap-3 sm:grid-cols-[8.5rem_minmax(0,1fr)]">
                <div className="space-y-1">
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label={imageUrl ? "Thay ảnh sản phẩm" : "Thêm ảnh sản phẩm"}
                    onDragOver={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    onDrop={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      const file = e.dataTransfer.files?.[0]
                      if (file) handleFileUpload(file)
                    }}
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && fileInputRef.current?.click()}
                    className={cn(
                      "relative flex aspect-square cursor-pointer items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-rule text-center transition-colors hover:border-accent hover:bg-accent/5",
                      imageUrl && "border-solid",
                      isUploadingImage && "pointer-events-none opacity-60",
                    )}
                  >
                    {isUploadingImage ? (
                      <Loader2 className="size-6 animate-spin text-accent" />
                    ) : imageUrl ? (
                      <img src={imageUrl} alt="Ảnh sản phẩm" className="h-full w-full object-cover" onError={(e) => ((e.target as HTMLElement).style.display = "none")} />
                    ) : (
                      <span className="space-y-1 px-1">
                        <UploadCloud className="mx-auto size-6 text-muted" />
                        <span className="block text-xs text-inkSoft">Thêm ảnh</span>
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-center gap-2 text-xs">
                    {imageUrl ? (
                      <button type="button" onClick={() => setImageUrl("")} className="min-h-11 desktop:min-h-0 cursor-pointer text-danger hover:underline">
                        Xóa ảnh
                      </button>
                    ) : (
                      <button type="button" onClick={() => setShowUrlInput(!showUrlInput)} className="min-h-11 desktop:min-h-0 cursor-pointer text-accent hover:underline">
                        {showUrlInput ? "Ẩn link" : "Dán link"}
                      </button>
                    )}
                  </div>
                </div>

                <div className="min-w-0 space-y-2.5">
                  <div className="grid grid-cols-[minmax(0,1fr)_6rem] items-end gap-2.5 sm:grid-cols-[minmax(0,1fr)_8rem]">
                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center justify-between gap-2">
                        <Label className="text-xs font-medium text-ink">
                          Mã SKU <span className="text-danger">*</span>
                        </Label>
                        <button
                          type="button"
                          onClick={handleGenerateSku}
                          className="text-xs font-medium text-accent hover:underline min-h-11 -my-2.5 py-2.5 desktop:min-h-0 desktop:my-0 desktop:py-0"
                        >
                          Tạo mã
                        </button>
                      </div>
                      <Input value={sku} onChange={(e) => setSku(e.target.value)} className="h-11 desktop:h-9 font-mono uppercase" required />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-medium text-ink">Đơn vị</Label>
                      {/* Gợi ý đơn vị thường dùng ngay trong ô (thay dãy nút chọn nhanh) */}
                      <Input value={unit} onChange={(e) => setUnit(e.target.value)} list="product-units" className="h-11 desktop:h-9" />
                      <datalist id="product-units">
                        {COMMON_UNITS.map((u) => (
                          <option key={u} value={u} />
                        ))}
                      </datalist>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-ink">
                      Tên sản phẩm <span className="text-danger">*</span>
                    </Label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} className="h-11 desktop:h-9 text-sm font-medium" required />
                  </div>
                </div>
              </div>

              {showUrlInput && !imageUrl && (
                <Input
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://…"
                  className="h-10 desktop:h-9 text-sm font-mono"
                />
              )}
            </div>

            {/* KHỐI 2: Giá & tồn kho | Vận chuyển — cạnh nhau trên máy tính */}
            <div className="grid grid-cols-1 items-stretch gap-3 lg:grid-cols-2">
              <div className="h-full rounded-xl bg-raise p-3 sm:p-4 space-y-2.5 shadow-card">
                <div className="flex items-baseline justify-between gap-2">
                  <h2 className="font-semibold text-sm text-ink">Giá & tồn kho</h2>
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-ink">Giá bán (₫)</Label>
                    <MoneyInput value={price} onValueChange={(v) => setPrice(v)} autoSelect className="h-11 desktop:h-9" />
                  </div>
                  {seesMoney && (
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-ink">Giá vốn (₫)</Label>
                    <MoneyInput value={cost} onValueChange={(v) => setCost(v)} autoSelect className="h-11 desktop:h-9" />
                  </div>
                  )}
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-ink">Tồn kho</Label>
                    <div className="relative">
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={stock === 0 ? "" : stock}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "")
                          setStock(val ? parseInt(val, 10) : 0)
                        }}
                        className="h-11 desktop:h-9 font-mono text-sm"
                      />
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted">{unit || "cái"}</span>
                    </div>
                  </div>
                  {/* Lãi / sản phẩm — một dòng thay ô phân tích riêng */}
                  {seesMoney && <div className="space-y-1">
                    <span className="block text-xs font-medium text-ink">Lãi / {unit || "cái"}</span>
                    <p className={cn("flex h-11 desktop:h-9 items-center font-mono text-sm font-semibold", profit >= 0 ? "text-ok" : "text-danger")}>
                      {profit >= 0 ? `+${money(profit)}` : money(profit)}
                      <span className="ml-1.5 text-xs font-medium">({profitMargin}%)</span>
                    </p>
                  </div>}
                </div>
              </div>

              <div className="h-full rounded-xl bg-raise p-3 sm:p-4 space-y-2.5 shadow-card">
                <div className="flex items-baseline justify-between gap-2">
                  <h2 className="font-semibold text-sm text-ink">Vận chuyển</h2>
                </div>
                {/* Hai hàng như thẻ Giá & tồn kho: [Cân | Tính cước] / [Dài | Rộng | Cao] */}
                <div className="grid grid-cols-2 gap-2.5">
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-ink">
                      Cân đóng gói (g) <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      list="weight-presets"
                      value={weightGram === 0 ? "" : weightGram}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "")
                        setWeightGram(val ? parseInt(val, 10) : 0)
                      }}
                      className="h-11 desktop:h-9 font-mono text-sm"
                      required
                    />
                    <datalist id="weight-presets">
                      {WEIGHT_PRESETS.map((p) => (
                        <option key={p.gram} value={p.gram}>
                          {p.label}
                        </option>
                      ))}
                    </datalist>
                  </div>
                  <div className="space-y-1">
                    <span className="block text-xs font-medium text-ink">Tính cước theo</span>
                    <p className="flex h-11 desktop:h-9 items-center font-mono text-sm font-semibold text-ink">
                      {(billableWeightGram / 1000).toLocaleString("vi-VN", { maximumFractionDigits: 2 })} kg
                      {volumetricGram !== null && <span className="ml-1.5 text-xs font-medium text-muted">(quy đổi {(volumetricGram / 1000).toLocaleString("vi-VN", { maximumFractionDigits: 2 })} kg)</span>}
                    </p>
                  </div>
                </div>
                {/* Quy đổi thể tích tắt (Super Admin) → không cần kích thước; số đã lưu giữ nguyên */}
                {volumetricRule.enabled && <div className="grid grid-cols-3 gap-2.5">
                  {([
                    ["Dài (cm)", lengthText, setLengthText],
                    ["Rộng (cm)", widthText, setWidthText],
                    ["Cao (cm)", heightText, setHeightText],
                  ] as const).map(([label, val, set]) => (
                    <div key={label} className="space-y-1">
                      <label className="block text-xs font-medium text-ink">{label}</label>
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={val}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => set(sanitizeDecimalInput(e.target.value))}
                        className="h-11 desktop:h-9 font-mono text-sm"
                      />
                    </div>
                  ))}
                </div>}
                {volumetricRule.enabled && roundedDims && (
                  <p className="text-xs text-warn">
                    Làm tròn thành {lengthCm ?? "—"} × {widthCm ?? "—"} × {heightCm ?? "—"} cm.
                  </p>
                )}
              </div>
            </div>
          </div>

        </div>

        {/* Điện thoại: nút lưu luôn trong tầm tay, không phải cuộn xuống cuối */}
        <MobileActionBar>
          <Link to="/products" className="inline-flex h-11 shrink-0 items-center px-2 text-sm text-muted hover:text-ink">
            Hủy
          </Link>
          <Button type="submit" disabled={saveMutation.isPending} className="h-11 flex-1 font-semibold">
            {saveMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : "Lưu"}
          </Button>
        </MobileActionBar>
      </form>
    </div>
  )
}

